/**
 * HeatmapGrid Component
 * SVG grid rendering for the activity heatmap (52 weeks x 7 days)
 */

import { useState, useMemo, useCallback } from 'react'
import {
  CELL_SIZE,
  CELL_GAP,
  WEEKS,
  DAYS_PER_WEEK,
  calculateIntensity,
  getColor,
  generateDateGrid,
  getMonthLabels,
  getDayLabels
} from './heatmapUtils'
import HeatmapTooltip from './HeatmapTooltip'

// Layout constants
const LABEL_WIDTH = 30
const MONTH_LABEL_HEIGHT = 15
const CELL_TOTAL = CELL_SIZE + CELL_GAP

/**
 * HeatmapGrid component - renders the SVG contribution grid
 * @param {Object} props - Component props
 * @param {Object} props.activities - Activity data keyed by date string
 * @param {number} props.maxDailyActivity - Maximum daily activity for intensity calculation
 * @param {boolean} props.isDark - Whether dark mode is active
 */
function HeatmapGrid({ activities = {}, maxDailyActivity = 0, isDark = false }) {
  const [tooltip, setTooltip] = useState({
    visible: false,
    data: null,
    date: null,
    position: { x: 0, y: 0 }
  })

  // Generate the date grid
  const weeks = useMemo(() => generateDateGrid(), [])
  const monthLabels = useMemo(() => getMonthLabels(weeks), [weeks])
  const dayLabels = getDayLabels()

  // Calculate SVG dimensions
  const svgWidth = LABEL_WIDTH + (WEEKS * CELL_TOTAL)
  const svgHeight = MONTH_LABEL_HEIGHT + (DAYS_PER_WEEK * CELL_TOTAL)

  // Handle cell hover
  const handleCellMouseEnter = useCallback((e, day) => {
    const rect = e.target.getBoundingClientRect()
    const activityData = activities[day.dateKey] || null

    setTooltip({
      visible: true,
      data: activityData,
      date: day.date,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top
      }
    })
  }, [activities])

  const handleCellMouseLeave = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }))
  }, [])

  return (
    <div className="relative">
      {/* Scrollable container for mobile */}
      <div className="overflow-x-auto pb-2" style={{ minWidth: '100%' }}>
        <svg
          width={svgWidth}
          height={svgHeight}
          className="block"
          style={{ minWidth: svgWidth }}
        >
          {/* Month labels */}
          <g className="month-labels">
            {monthLabels.map((label, index) => (
              <text
                key={`month-${index}`}
                x={LABEL_WIDTH + (label.weekIndex * CELL_TOTAL)}
                y={10}
                className="fill-gray-500 dark:fill-gray-400"
                style={{ fontSize: '10px' }}
              >
                {label.label}
              </text>
            ))}
          </g>

          {/* Day labels (Mon, Wed, Fri) */}
          <g className="day-labels">
            {dayLabels.map((label) => (
              <text
                key={`day-${label.dayIndex}`}
                x={0}
                y={MONTH_LABEL_HEIGHT + (label.dayIndex * CELL_TOTAL) + CELL_SIZE / 2 + 3}
                className="fill-gray-500 dark:fill-gray-400"
                style={{ fontSize: '9px' }}
              >
                {label.label}
              </text>
            ))}
          </g>

          {/* Activity grid cells */}
          <g className="activity-grid" transform={`translate(${LABEL_WIDTH}, ${MONTH_LABEL_HEIGHT})`}>
            {weeks.map((week, weekIndex) => (
              <g key={`week-${weekIndex}`} transform={`translate(${weekIndex * CELL_TOTAL}, 0)`}>
                {week.map((day, dayIndex) => {
                  // Skip future dates
                  if (day.isFuture) return null

                  const activity = activities[day.dateKey]
                  const count = activity?.count || 0
                  const intensity = calculateIntensity(count, maxDailyActivity)
                  const color = getColor(intensity, isDark)

                  return (
                    <rect
                      key={`cell-${weekIndex}-${dayIndex}`}
                      x={0}
                      y={dayIndex * CELL_TOTAL}
                      width={CELL_SIZE}
                      height={CELL_SIZE}
                      rx={2}
                      ry={2}
                      fill={color}
                      className="cursor-pointer transition-opacity hover:opacity-80"
                      onMouseEnter={(e) => handleCellMouseEnter(e, day)}
                      onMouseLeave={handleCellMouseLeave}
                    />
                  )
                })}
              </g>
            ))}
          </g>
        </svg>
      </div>

      {/* Tooltip */}
      <HeatmapTooltip
        data={tooltip.data}
        date={tooltip.date}
        position={tooltip.position}
        visible={tooltip.visible}
      />
    </div>
  )
}

export default HeatmapGrid
